import { createContext, useContext, type Provider } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';

import { type UIConfig } from '../../../types';
import {
	SPLIT_LAYOUT_ADJACENT_MIN_WIDTH,
	SPLIT_LAYOUT_INITIAL_WIDTH,
	SPLIT_LAYOUT_MIN_WIDTH,
} from '../../constants/private';

const initialState: UIConfig = {
	fullScreen: false,
	externalEmbed: false,
	isSplitLayout: false,
	splitLayoutInitialWidth: SPLIT_LAYOUT_INITIAL_WIDTH,
	splitLayoutMinWidth: SPLIT_LAYOUT_MIN_WIDTH,
	splitLayoutAdjacentMinWidth: SPLIT_LAYOUT_ADJACENT_MIN_WIDTH,
	splitLayoutStorageKey: 'conversation-assistant.split-layout.right-width',
	customHandlers: {
		handleDeepLinkClick: undefined,
		handleSubmitSmartCreate: () => {},
	},
	isBitbucketBetaEnabled: false,
	customFeedbackCategories: undefined,
	stagingAreaRef: undefined,
	promptInputDroppableUrls: undefined,
	enableBrowserContextBanner: false,
	disableSettingsModal: false,
	enableSkills: true,
	browserContextSanitizer: undefined,
	elements: {
		closeButton: true,
		newChatButton: true,
		fullscreenButton: true,
		createAgentButton: true,
		browseAgentsButton: true,
		agentsMenu: true,
		navbar: true,
		mainChatHeader: true,
		stagingAreaEnabled: false,
		voiceDictationButton: false,
		fileUploadButton: true,
		chatModeMenuButton: true,
		shouldShowConversationActions: true,
	},
};

export const uiConfigDefaults: UIConfig = initialState;

const uiConfig = createContext<UIConfig>(initialState);
export const UIConfigProvider: Provider<UIConfig> = uiConfig.Provider;
export const useUIConfig = (): UIConfig => useContext(uiConfig);

export const createUIConfig = (config?: Partial<UIConfig>): UIConfig => {
	// Allow fullscreen override for branch deploy testing purposes
	const processedConfig: Partial<UIConfig> | undefined =
		localStorage.getItem('DEBUG_rovo_chat_fullscreen') === 'true' &&
		fg('rovo_chat_show_local_frontend_components')
			? {
					...config,
					fullScreen: true,
				}
			: config;

	return {
		...{
			...initialState,
			...processedConfig,
			enableSkills: processedConfig?.enableSkills ?? initialState.enableSkills,
			enableBrowserContextBanner:
				processedConfig?.enableBrowserContextBanner ?? initialState.enableBrowserContextBanner,
			disableSettingsModal:
				processedConfig?.disableSettingsModal ?? initialState.disableSettingsModal,
			browserContextSanitizer: processedConfig?.browserContextSanitizer,
			elements: {
				...initialState.elements,
				...processedConfig?.elements,
			},
			promptInputDroppableUrls: processedConfig?.promptInputDroppableUrls,
		},
	};
};
